import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { Modal } from 'antd';
import { QRCodeSVG } from 'qrcode.react';
import { DownloadOutlined, PrinterOutlined, DeleteOutlined, SortAscendingOutlined, SortDescendingOutlined } from '@ant-design/icons';

// 아이콘 컴포넌트
const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);

const LateIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
  </svg>
);

const AbsentIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
  </svg>
);

// 타임스탬프를 포맷팅하는 헬퍼 함수 추가
const formatTimestamp = (timestamp) => {
  if (!timestamp) return '-';
  
  try {
    const date = new Date(timestamp);
    
    // 날짜가 유효하지 않은 경우
    if (isNaN(date.getTime())) return timestamp;
    
    // 년-월-일 시:분 형식으로 포맷팅
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return timestamp;
  }
};

const AttendanceManagement = () => {
  const { user, authAxios } = useAuth();
  const [attendanceList, setAttendanceList] = useState([]);
  const [classesLoading, setClassesLoading] = useState(true);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [error, setError] = useState(null);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [allClassroomStudents, setAllClassroomStudents] = useState([]); // 강의실에 소속된 모든 교육생
  const [enrolledStudentIds, setEnrolledStudentIds] = useState([]); // 현재 강의에 등록된 교육생 ID
  const [selectedClass, setSelectedClass] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // 강의 목록 관련 상태 추가
  const [classSearchQuery, setClassSearchQuery] = useState('');
  const [classFilter, setClassFilter] = useState({
    category: 'all',
    instructor: 'all',
    classroom: 'all'  // 강의실 필터 추가
  });
  const [classSortConfig, setClassSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  
  // 새로운 필터 상태
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('all'); // 전체, 1기, 2기 등 기수별 탭
  const [cohortTabs, setCohortTabs] = useState(['all']);
  
  // 정렬 관련 상태 - 출석 목록 테이블용
  const [attendanceSortConfig, setAttendanceSortConfig] = useState({ 
    key: null, 
    direction: 'asc' 
  });
  
  const [isAddingAttendance, setIsAddingAttendance] = useState(false);
  const [isEditingAttendance, setIsEditingAttendance] = useState(false);
  const [currentAttendance, setCurrentAttendance] = useState(null);
  const [attendanceSummary, setAttendanceSummary] = useState({
    total: 0,
    present: 0,
    late: 0,
    absent: 0,
    earlyLeave: 0
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  
  // 시간 수정 관련 상태
  const [isEditingTime, setIsEditingTime] = useState(false);
  const [timeEditData, setTimeEditData] = useState({ 
    attendanceId: null, 
    studentId: null,
    isNew: false,
    type: null, // 'checkIn' 또는 'checkOut'
    time: '',
    date: ''
  });
  
  // QR 코드 관련 상태
  const [showQRModal, setShowQRModal] = useState(false);
  const [currentQRCode, setCurrentQRCode] = useState('');
  const [qrLoading, setQrLoading] = useState(false);
  const [qrType, setQrType] = useState('checkin'); // 'checkin' 또는 'checkout'
  
  // 사용자 권한 확인
  const isInstructor = user?.roles?.includes('instructor') && !user?.roles?.includes('admin');
  
  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm();
  const selectedStatus = watch('status');

  // 상태 값 변환 (프론트엔드 <-> 백엔드)
  const convertStatusForBackend = (status) => {
    if (!status) return "결석";
    
    const statusMap = {
      'present': "출석",
      'absent': "결석",
      'late': "지각",
      'earlyLeave': "조퇴"
    };
    
    return statusMap[status] || "결석";
  };

  const convertStatusForFrontend = (status) => {
    if (!status) return 'absent';
    
    const statusStr = String(status).toLowerCase();
    
    const statusMap = {
      "출석": 'present',
      "결석": 'absent',
      "지각": 'late',
      "조퇴": 'earlyLeave',
      "present": 'present',
      "absent": 'absent',
      "late": 'late',
      "early_leave": 'earlyLeave'
    };
    
    return statusMap[statusStr] || 'absent';
  };

  // 출석 방법 변환 (프론트엔드 <-> 백엔드)
  const convertMethodForBackend = (method) => {
    if (!method) return "수기등록";
    
    const methodMap = {
      'qr': "QR인증",
      'admin': "관리자등록",
      'manual': "수기등록",
      'manualEdit': "수기등록"
    };
    
    return methodMap[method] || "수기등록";
  };

  const convertMethodForFrontend = (method) => {
    if (!method) return 'manual';
    
    const methodStr = String(method);
    
    const methodMap = {
      "QR인증": 'qr',
      "관리자등록": 'admin',
      "수기등록": 'manual'
    };
    
    return methodMap[methodStr] || 'manual';
  };

  // 출석 기록 가져오기
  const fetchAttendanceRecords = async () => {
    if (!selectedClass) return;
    
    try {
      setAttendanceLoading(true);
      
      // 수정된 API 호출
      const studentsResponse = await authAxios.get(`/classes/${selectedClass}/students`);
      
      // 수정된 출석 API 호출
      const attendanceResponse = await authAxios.get(`/attendance/by-class/${selectedClass}`);
      
      // 필요한 정보 추출
      const studentsList = studentsResponse.data;
      const attendanceData = attendanceResponse.data;
      
      console.log('=== 출석 데이터 로그 ===');
      console.log('1. 학생 목록:', {
        total: studentsList.length,
        students: studentsList.map(s => ({
          id: s.id || s._id,
          name: s.name,
          batch: s.batch
        }))
      });
      
      console.log('2. 출석 데이터:', {
        total: attendanceData.length,
        records: attendanceData.map(a => ({
          id: a._id || a.id,
          studentId: a.studentId,
          status: a.status,
          method: a.method,
          checkInTime: a.checkInTime,
          checkOutTime: a.checkOutTime,
          registeredBy: a.registeredBy,
          registeredByName: a.registeredByName,
          location: a.registeredLocation
        }))
      });
      
      console.log('3. API 응답 상세:', {
        studentsResponse: {
          status: studentsResponse.status,
          headers: studentsResponse.headers,
          config: studentsResponse.config
        },
        attendanceResponse: {
          status: attendanceResponse.status,
          headers: attendanceResponse.headers,
          config: attendanceResponse.config
        }
      });
      console.log('=====================');
      
      // 모든 학생에 대한 출석 목록 생성
      const fullAttendanceList = studentsList.map(student => {
        // 해당 학생의 출석 기록 찾기
        const existingRecord = attendanceData.find(record => 
          record.studentId === student.id || record.studentId === student._id);
        
        if (existingRecord) {
          // 기존 출석 기록이 있으면 그대로 사용
          const method = existingRecord.method || 'manual';
          
          // registeredBy 정보 처리
          let registeredByName = '시스템';
          if (existingRecord.registeredByName) {
            registeredByName = existingRecord.registeredByName;
          } else if (existingRecord.registeredBy) {
            if (typeof existingRecord.registeredBy === 'object' && existingRecord.registeredBy.name) {
              registeredByName = existingRecord.registeredBy.name;
            } else if (typeof existingRecord.registeredBy === 'string') {
              registeredByName = existingRecord.registeredBy;
            }
          }
          
          // GPS 정보 처리
          let locationInfo = '';
          if (existingRecord.registeredLocation) {
            const loc = existingRecord.registeredLocation;
            if (loc.latitude && loc.longitude) {
              locationInfo = `[${loc.latitude.toFixed(6)}, ${loc.longitude.toFixed(6)}]`;
            }
          }
          
          // 학생의 기수 정보 추출
          const batch = student.batch || 'all';
          
          // 입실/퇴실 시간 포맷팅
          const checkInTime = existingRecord.checkInTime ? formatTimestamp(existingRecord.checkInTime) : '-';
          const checkOutTime = existingRecord.checkOutTime ? formatTimestamp(existingRecord.checkOutTime) : '-';
          
          // 출석 상태 변경 (입실, 퇴실 시간이 모두 있어야 출석)
          let status = 'absent';
          if (checkInTime !== '-' && checkOutTime !== '-') {
            status = 'present';
          }
          
          return {
            ...existingRecord,
            className: classes.find(c => c.id === selectedClass || c._id === selectedClass)?.name || '',
            id: existingRecord._id || existingRecord.id,
            status: status,
            method: convertMethodForFrontend(existingRecord.method),
            studentName: student.name,
            registeredByName: registeredByName,
            locationInfo: locationInfo,
            checkInTime,
            checkOutTime,
            batch
          };
        } else {
          // 기존 출석 기록이 없으면 기본값으로 생성
          const batch = student.batch || 'all';
          
          return {
            id: null,
            studentId: student._id || student.id,
            studentName: student.name,
            classId: selectedClass,
            className: classes.find(c => c.id === selectedClass || c._id === selectedClass)?.name || '',
            status: 'absent',
            method: 'manual',
            date: new Date().toISOString().split('T')[0],
            checkInTime: '-',
            checkOutTime: '-',
            isNew: true,
            registeredBy: null,
            registeredByName: null,
            locationInfo: '',
            batch
          };
        }
      });
      
      // 출석 목록 설정
      setAttendanceList(fullAttendanceList);
      
      // 기수 목록 설정
      const uniqueBatches = [...new Set(fullAttendanceList.map(a => a.batch))].filter(Boolean);
      setCohortTabs(['all', ...uniqueBatches.sort()]);
      
      // 전체 데이터 기준으로 출석 통계 계산
      updateAttendanceSummary(fullAttendanceList, activeTab);
      
      setAttendanceLoading(false);
    } catch (err) {
      console.error('Attendance records fetch error:', err);
      toast.error('출석 기록을 불러오는 중 오류가 발생했습니다.');
      setAttendanceLoading(false);
    }
  };

  // 탭에 따른 출석 통계 업데이트
  const updateAttendanceSummary = (attendanceData, tab) => {
    // 필터링된 데이터
    const filteredData = tab === 'all' 
      ? attendanceData 
      : attendanceData.filter(a => a.batch === tab);
    
    // 출석 통계 계산
    setAttendanceSummary({
      total: filteredData.length,
      present: filteredData.filter(a => a.status === 'present').length,
      late: filteredData.filter(a => a.status === 'late').length,
      absent: filteredData.filter(a => a.status === 'absent').length,
      earlyLeave: filteredData.filter(a => a.status === 'earlyLeave').length
    });
  };

  // 강의 목록 가져오기
  const fetchClasses = async () => {
    try {
      setClassesLoading(true);
      setError(null);
      
      // API 호출 - 모든 강의 목록 가져오기
      const response = await authAxios.get('/classes');
      let classesData = response.data;
      
      // 현재 로그인한 사용자가 강사인 경우, 본인이 강사로 등록된 강의만 필터링
      if (user && user.roles && user.roles.includes('instructor') && !user.roles.includes('admin')) {
        const instructorId = user.userId || user._id;
        classesData = classesData.filter(classItem => {
          if (classItem.instructors && Array.isArray(classItem.instructors)) {
            return classItem.instructors.some(instructor => 
              instructor === instructorId || 
              (typeof instructor === 'object' && (instructor.id === instructorId || instructor._id === instructorId))
            );
          }
          if (classItem.instructorId) {
            return classItem.instructorId === instructorId || 
                  (typeof classItem.instructorId === 'object' && 
                   (classItem.instructorId.id === instructorId || classItem.instructorId._id === instructorId));
          }
          return false;
        });
      }
      
      // 강사 정보가 없는 경우 강사 정보 조회
      const classesWithInstructorInfo = await Promise.all(classesData.map(async (classItem) => {
        let updatedClassItem = { ...classItem };
        
        // 강사 정보 추가
        if (!updatedClassItem.instructorName && updatedClassItem.instructorId) {
          try {
            const instructorResponse = await authAxios.get(`/users/${updatedClassItem.instructorId}`);
            if (instructorResponse.data) {
              updatedClassItem.instructorName = instructorResponse.data.name || '강사미배정';
            }
          } catch (error) {
            console.error('강사 정보 조회 실패:', error);
          }
        }
        
        // 강의실 정보 추가 
        if (!updatedClassItem.classroomName && updatedClassItem.classroomId) {
          try {
            const classroomResponse = await authAxios.get(`/classrooms/${updatedClassItem.classroomId}`);
            if (classroomResponse.data) {
              updatedClassItem.classroom = classroomResponse.data;
              updatedClassItem.classroomName = classroomResponse.data.name || '강의실정보없음';
            }
          } catch (error) {
            console.error('강의실 정보 조회 실패:', error);
          }
        }
        
        // 출석 통계 정보 추가
        try {
          // 학생 목록 가져오기
          const studentsResponse = await authAxios.get(`/classes/${updatedClassItem.id || updatedClassItem._id}/students`);
          const students = studentsResponse.data;
          const totalStudents = students.length;
          
          // 출석 정보 가져오기
          const attendanceResponse = await authAxios.get(`/attendance/by-class/${updatedClassItem.id || updatedClassItem._id}`);
          const attendanceData = attendanceResponse.data;
          
          console.log(`강의 [${updatedClassItem.name}] 출석 데이터:`, attendanceData);
          
          // 학생별 출석 상태 확인
          let presentCount = 0;
          
          // 각 학생별로 출석 데이터 확인
          for (const student of students) {
            const studentId = student.id || student._id;
            // 해당 학생의 출석 데이터 찾기
            const attendance = attendanceData.find(a => 
              a.studentId === studentId || 
              (typeof a.studentId === 'object' && (a.studentId.id === studentId || a.studentId._id === studentId))
            );
            
            if (attendance) {
              // 백엔드와 프론트엔드의 다양한 출석 상태 표기 고려
              const status = attendance.status?.toLowerCase?.() || "";
              const isPresent = status === "출석" || 
                               status === "present" || 
                               status === "attended" ||
                               (attendance.checkInTime && attendance.checkOutTime); // 입실, 퇴실 시간이 모두 있으면 출석으로 간주
              
              if (isPresent) {
                presentCount++;
              }
            }
            // attendance가 없는 경우는 자동으로 결석으로 간주됨 (카운트 증가 없음)
          }
          
          // 전체 학생 수와 실제 출석 학생 수 로깅
          console.log(`강의 [${updatedClassItem.name}] 통계: 전체 학생 ${totalStudents}명, 출석 인정 ${presentCount}명, 결석 ${totalStudents - presentCount}명`);
          
          // 출석 통계 추가
          updatedClassItem.attendanceStats = {
            total: totalStudents,
            present: presentCount,
            absent: totalStudents - presentCount
          };
        } catch (error) {
          console.error(`강의 [${updatedClassItem.name}] 출석 통계 정보 조회 실패:`, error);
          updatedClassItem.attendanceStats = {
            total: 0,
            present: 0,
            absent: 0
          };
        }
        
        // 개발용 로그 - 강의실 데이터 확인
        console.log('강의 정보:', {
          id: updatedClassItem.id || updatedClassItem._id,
          name: updatedClassItem.name,
          classroomId: updatedClassItem.classroomId,
          classroom: updatedClassItem.classroom,
          classroomName: updatedClassItem.classroomName,
          batch: updatedClassItem.batch || updatedClassItem.batchNumber || '기수정보없음',
          attendanceStats: updatedClassItem.attendanceStats
        });
        
        return updatedClassItem;
      }));
      setClasses(classesWithInstructorInfo);

      // === 최근 날짜 및 출결 기준으로 강의 자동 선택 ===
      if (classesWithInstructorInfo.length > 0 && !selectedClass) {
        // 1. 가장 최근 날짜 찾기
        const maxDate = classesWithInstructorInfo.reduce((max, c) => {
          const d = c.date ? new Date(c.date) : null;
          if (!d || isNaN(d.getTime())) return max;
          return (!max || d > max) ? d : max;
        }, null);
        // 2. 최근 날짜의 강의들만 추출
        const recentClasses = classesWithInstructorInfo.filter(c => {
          if (!c.date) return false;
          const d = new Date(c.date);
          return maxDate && d.getTime() === maxDate.getTime();
        });
        // 3. 출결 기록이 가장 최근인 강의 찾기
        let classToSelect = recentClasses[0];
        let latestAttendanceTime = null;
        for (const c of recentClasses) {
          try {
            const attRes = await authAxios.get(`/attendance/by-class/${c.id || c._id}`);
            const records = attRes.data || [];
            // 출결 기록 중 가장 최근 시간 찾기 (checkInTime, checkOutTime)
            const latest = records.reduce((latest, rec) => {
              const times = [rec.checkInTime, rec.checkOutTime].filter(Boolean).map(t => new Date(t));
              for (const t of times) {
                if (!latest || t > latest) latest = t;
              }
              return latest;
            }, null);
            if (latest && (!latestAttendanceTime || latest > latestAttendanceTime)) {
              latestAttendanceTime = latest;
              classToSelect = c;
            }
          } catch (err) {
            // 출결 기록이 없으면 무시
          }
        }
        setSelectedClass(classToSelect.id || classToSelect._id);
      }
      setClassesLoading(false);
    } catch (err) {
      console.error('Classes fetch error:', err);
      setError('강의 목록을 불러오는 중 오류가 발생했습니다.');
      setClassesLoading(false);
    }
  };

  // 학생 목록 가져오기 - 강의실 기반으로 모든 학생을 가져오도록 변경
  const fetchStudents = async () => {
    if (!selectedClass) return;
    
    try {
      setAttendanceLoading(true);
      
      // 선택한 강의 정보 가져오기
      const selectedClassInfo = classes.find(c => c.id === selectedClass || c._id === selectedClass);
      if (!selectedClassInfo) {
        toast.error('강의 정보를 찾을 수 없습니다.');
        setIsLoading(false);
        return;
      }
      
      // 강의실 ID 확인
      const classroomId = selectedClassInfo.classroomId || 
                        selectedClassInfo.classroom?._id || 
                        selectedClassInfo.classroom?.id;
      
      if (!classroomId) {
        toast.error('강의실 정보를 찾을 수 없습니다.');
        setIsLoading(false);
        return;
      }
      
      console.log(`선택된 강의: ${selectedClassInfo.name}, 강의실 ID: ${classroomId}`);
      
      // 1. 현재 강의에 등록된 학생 목록 가져오기
      const enrolledStudentsResponse = await authAxios.get(`/classes/${selectedClass}/students`);
      const enrolledStudents = enrolledStudentsResponse.data;
      
      // 등록된 학생 ID 목록 저장
      const enrolledIds = enrolledStudents.map(student => student.id || student._id);
      setEnrolledStudentIds(enrolledIds);
      
      console.log(`강의에 등록된 학생 수: ${enrolledStudents.length}`);
      
      // 2. 강의실 정보 가져오기
      const classroomResponse = await authAxios.get(`/classrooms/${classroomId}`);
      const classroomInfo = classroomResponse.data;
      
      if (!classroomInfo) {
        toast.error('강의실 정보를 가져올 수 없습니다.');
        setIsLoading(false);
        return;
      }
      
      console.log(`강의실 정보: ${classroomInfo.name}, 구분: ${classroomInfo.category || '미지정'}`);
      
      // 3. 교육장 정보와 일치하는 모든 학생 가져오기
      const allStudentsResponse = await authAxios.get('/students');
      const allStudents = allStudentsResponse.data;
      
      // 강의실 구분과 학생의 교육장이 일치하는 학생들 필터링
      const classroomCategory = classroomInfo.category || classroomInfo.type || '';
      let matchedStudents = allStudents;
      
      if (classroomCategory) {
        console.log(`강의실 구분: ${classroomCategory}`);
        matchedStudents = allStudents.filter(student => {
          // 학생의 교육장(campus) 필드 또는 기타 분류 필드가 강의실 구분과 일치하는지 확인
          const studentCampus = student.campus || '';
          console.log(`교육생 : ${student.name}, 교육장: ${studentCampus}, 포함여부: ${studentCampus.includes(classroomCategory) || classroomCategory.includes(studentCampus)}, 강의 구분: ${selectedClassInfo.category}, 학생 구분: ${student.category}, 구분판단: ${selectedClassInfo.category.includes(student.category)}, 최종 판단: ${(studentCampus.includes(classroomCategory) || classroomCategory.includes(studentCampus)) && selectedClassInfo.category.includes(student.category)}`);
          return (studentCampus.includes(classroomCategory) || classroomCategory.includes(studentCampus)) && selectedClassInfo.category.includes(student.category);
        });
      }
      
      console.log(`강의실 구분과 일치하는 학생 수: ${matchedStudents.length}`);
      
      // 전체 학생 목록 저장
      setAllClassroomStudents(matchedStudents);
      
      // 기존 학생 목록도 유지 (기존 코드 호환성)
      setStudents(enrolledStudents);
      
      setAttendanceLoading(false);
    } catch (err) {
      console.error('Students fetch error:', err);
      toast.error('학생 목록을 불러오는 중 오류가 발생했습니다.');
      setAttendanceLoading(false);
    }
  };

  // 컴포넌트 마운트 시 데이터 로딩
  useEffect(() => {
    fetchClasses();
    
    // 로컬 스토리지에서 이전에 선택한 강의 ID 가져오기
    const savedClassId = localStorage.getItem('selectedClassId');
    if (savedClassId) {
      setSelectedClass(savedClassId);
    }
  }, []);

  // 선택된 강의가 변경되면 출석 목록 다시 로딩
  useEffect(() => {
    if (selectedClass) {
      fetchAttendanceRecords();
      fetchStudents();
      
      // 선택된 강의 ID를 로컬 스토리지에 저장
      localStorage.setItem('selectedClassId', selectedClass);
    }
  }, [selectedClass]);

  // 탭이 변경되면 출석 통계 업데이트
  useEffect(() => {
    updateAttendanceSummary(attendanceList, activeTab);
  }, [activeTab, attendanceList]);

  // 정렬 기능
  const requestSort = (key) => {
    let direction = 'asc';
    if (classSortConfig.key === key && classSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setClassSortConfig({ key, direction });
  };

  // 출석 목록 정렬 기능
  const requestAttendanceSort = (key) => {
    let direction = 'asc';
    if (attendanceSortConfig.key === key && attendanceSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setAttendanceSortConfig({ key, direction });
  };

  // 검색 및 필터링된 출석 목록
  const filteredAttendanceList = useMemo(() => {
    // 기본 데이터 복사
    let filteredList = [...attendanceList];
    
    // 기수별 필터링 (탭)
    if (activeTab !== 'all') {
      filteredList = filteredList.filter(attendance => attendance.batch === activeTab);
    }
    
    // 출석 상태로 필터링
    if (statusFilter !== 'all') {
      filteredList = filteredList.filter(attendance => attendance.status === statusFilter);
    }

    // 등록 방법으로 필터링
    if (methodFilter !== 'all') {
      filteredList = filteredList.filter(attendance => attendance.method === methodFilter);
    }
      
    // 검색어 필터링
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredList = filteredList.filter(attendance => (
        attendance.studentName.toLowerCase().includes(query) ||
        attendance.className.toLowerCase().includes(query)
      ));
    }
    
    // 정렬 적용
    if (attendanceSortConfig.key) {
      filteredList.sort((a, b) => {
        // null 값 처리
        if (!a[attendanceSortConfig.key]) return attendanceSortConfig.direction === 'asc' ? -1 : 1;
        if (!b[attendanceSortConfig.key]) return attendanceSortConfig.direction === 'asc' ? 1 : -1;
        
        // 타입에 따른 정렬
        if (typeof a[attendanceSortConfig.key] === 'string') {
          if (attendanceSortConfig.direction === 'asc') {
            return a[attendanceSortConfig.key].localeCompare(b[attendanceSortConfig.key]);
          } else {
            return b[attendanceSortConfig.key].localeCompare(a[attendanceSortConfig.key]);
          }
        } else {
          if (attendanceSortConfig.direction === 'asc') {
            return a[attendanceSortConfig.key] - b[attendanceSortConfig.key];
          } else {
            return b[attendanceSortConfig.key] - a[attendanceSortConfig.key];
          }
        }
      });
    }
    
    return filteredList;
  }, [attendanceList, activeTab, statusFilter, methodFilter, searchQuery, attendanceSortConfig]);
  
  // 교육생 목록을 등록 여부로 구분하여 정렬
  const sortedClassroomStudents = useMemo(() => {
    if (!allClassroomStudents.length) return [];
    
    // 등록된 학생과 미등록 학생 분리
    const enrolled = [];
    const notEnrolled = [];
    
    // 학생 객체에 출석 정보 추가하기
    allClassroomStudents.forEach(student => {
      const isEnrolled = enrolledStudentIds.includes(student.id || student._id);
      
      // 해당 학생의 출석 기록 찾기
      const attendanceRecord = attendanceList.find(
        a => a.studentId === (student.id || student._id)
      );
      
      // 새로운 객체를 만들어서 정보 추가
      const studentWithAttendance = {
        ...student,
        isEnrolled,
        attendanceRecord
      };
      
      if (isEnrolled) {
        enrolled.push(studentWithAttendance);
      } else {
        notEnrolled.push(studentWithAttendance);
      }
    });
    
    // 컬럼 정렬 함수 정의
    const sortByColumn = (a, b, key) => {
      // 학생 이름으로 정렬하는 경우
      if (key === 'studentName') {
        return a.name.localeCompare(b.name);
      }
      
      // 다른 컬럼으로 정렬하는 경우 (출석 정보 기반)
      const aValue = a.attendanceRecord?.[key];
      const bValue = b.attendanceRecord?.[key];
      
      // 값이 없는 경우 처리
      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;
      
      // 문자열 타입
      if (typeof aValue === 'string') {
        return aValue.localeCompare(bValue);
      }
      
      // 숫자 타입
      return aValue - bValue;
    };
    
    // 각 그룹 내에서 정렬
    if (attendanceSortConfig.key) {
      // 정렬 방향 고려
      const direction = attendanceSortConfig.direction;
      const key = attendanceSortConfig.key;
      
      enrolled.sort((a, b) => {
        let result = sortByColumn(a, b, key);
        return direction === 'asc' ? result : -result;
      });
      
      notEnrolled.sort((a, b) => {
        let result = sortByColumn(a, b, key);
        return direction === 'asc' ? result : -result;
      });
    } else {
      // 기본 정렬: 이름순
      enrolled.sort((a, b) => a.name.localeCompare(b.name));
      notEnrolled.sort((a, b) => a.name.localeCompare(b.name));
    }
    
    // 등록된 학생이 먼저 오도록 결합
    return [...enrolled, ...notEnrolled];
  }, [allClassroomStudents, enrolledStudentIds, attendanceList, attendanceSortConfig]);

  // 출석 상태 변경
  const handleStatusChange = async (attendance, newStatus) => {
    try {
      // 현재 상태와 같은 상태로 변경하려는 경우 무시
      if (attendance.status === newStatus) return;

      // 상태 변경 확인 메시지
      let confirmMessage = '';
      if (newStatus === 'present') {
        confirmMessage = '상태를 출석으로 변경하시겠습니까?';
      } else if (newStatus === 'late') {
        confirmMessage = '상태를 지각으로 변경하시겠습니까?';
      } else if (newStatus === 'earlyLeave') {
        confirmMessage = '상태를 조퇴로 변경하시겠습니까?';
      } else {
        confirmMessage = '상태를 결석으로 변경하시겠습니까?\n출석 기록이 삭제됩니다.';
      }

      if (!window.confirm(confirmMessage)) return;

      if (newStatus !== 'absent') {
        // 출석, 지각, 조퇴로 변경
        const now = new Date();
        const timestamp = now.toISOString();
        const today = now.toISOString().split('T')[0];
        
        // 상태 값을 백엔드 형식으로 변환
        const backendStatus = convertStatusForBackend(newStatus);
        
        // 사용자 ID와 이름 가져오기
        const userId = user?.id || user?._id;
        const userName = user?.name || user?.username || '관리자';
        
        // 현재 위치 정보 가져오기
        let location = null;
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject);
          });
          
          location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
        } catch (geoErr) {
          console.warn('위치 정보를 가져올 수 없습니다:', geoErr);
          location = { latitude: 0, longitude: 0 };
        }
        
        // 현재 강의 정보 가져오기 - classroomId를 얻기 위함
        const classInfo = classes.find(c => c.id === selectedClass || c._id === selectedClass);
        if (!classInfo) {
          toast.error('강의 정보를 찾을 수 없습니다.');
          return;
        }
        
        // 강의실 ID 설정
        const classroomId = classInfo.classroomId || 
                           classInfo.classroom?._id || 
                           classInfo.classroom?.id || 
                           selectedClass; // 최악의 경우 classId로 대체
        
        // 입실/퇴실 시간 설정
        const updateData = {
          studentId: attendance.studentId,
          classId: selectedClass,
          classroomId: classroomId, // 반드시 classroomId 설정
          date: today,
          status: backendStatus,
          method: convertMethodForBackend('admin'),
          registeredBy: userId,
          registeredByName: userName,
          registeredLocation: location,
          timestamp: timestamp
        };
        
        // 최초 등록 또는 입실 시간이 없는 경우 입실 시간 설정
        if (attendance.isNew || attendance.checkInTime === '-') {
          updateData.checkInTime = timestamp;
        }
        
        // 조퇴인 경우 퇴실 시간 설정
        if (newStatus === 'earlyLeave') {
          updateData.checkOutTime = timestamp;
        }
        
        console.log('출석 데이터 업데이트:', updateData);
        
        if (attendance.isNew) {
          // 새 출석 기록 생성
          const response = await authAxios.post('/attendance', updateData);
          console.log('API 응답:', response.data);
        } else {
          // 기존 출석 기록 업데이트
          const response = await authAxios.put(`/attendance/${attendance.id}`, updateData);
          console.log('API 응답:', response.data);
        }
      } else {
        // 결석으로 변경 (기록 삭제)
        if (!attendance.isNew) {
          await authAxios.delete(`/attendance/${attendance.id}`);
        }
      }
      
      // 성공 시 출석 목록 새로고침
      await fetchAttendanceRecords();
      
      // 상태 변경 성공 메시지
      const statusDisplayNames = {
        'present': '출석',
        'absent': '결석',
        'late': '지각',
        'earlyLeave': '조퇴'
      };
      
      toast.success(`출석 상태가 ${statusDisplayNames[newStatus] || newStatus}(으)로 변경되었습니다.`);
    } catch (err) {
      console.error('Error updating attendance status:', err);
      
      // 더 상세한 오류 정보 로깅
      if (err.response) {
        console.error('서버 응답:', err.response.data);
        console.error('상태 코드:', err.response.status);
        console.error('헤더:', err.response.headers);
        
        // 서버에서 오류 메시지를 제공하는 경우 이를 사용
        const errorMessage = err.response.data.message || '출석 상태 업데이트 중 오류가 발생했습니다.';
        toast.error(errorMessage);
      } else if (err.request) {
        console.error('요청 정보:', err.request);
        toast.error('서버에 요청을 보냈으나 응답이 없습니다.');
      } else {
        console.error('에러 메시지:', err.message);
        toast.error('출석 상태 업데이트 중 오류가 발생했습니다.');
      }
    }
  };

  // 시간 셀 클릭 핸들러 (수기 변경 모달 열기)
  const handleTimeClick = (attendance, type) => {
    // 현재 시간 기본값 설정
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const defaultTime = `${hours}:${minutes}`;
    
    // 시간 정보 추출
    const currentTime = type === 'checkIn' ? attendance.checkInTime : attendance.checkOutTime;
    let timeValue = defaultTime; // 기본값으로 현재 시간 설정
    
    // '-'가 아닌 경우에만 시간 파싱
    if (currentTime !== '-') {
      try {
        const date = new Date(currentTime);
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        timeValue = `${hours}:${minutes}`;
      } catch (error) {
        console.error('시간 파싱 오류:', error);
      }
    }
    
    // 모달에 필요한 데이터 설정
    setTimeEditData({
      attendanceId: attendance.id,
      studentId: attendance.studentId,
      isNew: attendance.isNew,
      type,
      time: timeValue,
      date: new Date().toISOString().split('T')[0]
    });
    
    // 모달 열기
    setIsEditingTime(true);
  };
  
  // 시간 수정 처리
  const handleTimeSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // 시간 값이 비어있는 경우
      if (!timeEditData.time) {
        toast.error('시간을 입력해주세요.');
        return;
      }
      
      // 시간 객체 생성
      const [hours, minutes] = timeEditData.time.split(':').map(Number);
      const date = new Date(timeEditData.date);
      date.setHours(hours, minutes, 0, 0);
      
      // 현재 출석 기록 찾기
      let attendance;
      if (timeEditData.isNew) {
        attendance = attendanceList.find(a => 
          a.studentId === timeEditData.studentId && a.isNew);
      } else {
        attendance = attendanceList.find(a => a.id === timeEditData.attendanceId);
      }
      
      if (!attendance) {
        toast.error('출석 기록을 찾을 수 없습니다.');
        return;
      }
      
      // 현재 GPS 정보 가져오기
      let location = { latitude: 0, longitude: 0 };
      
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            timeout: 5000,
            enableHighAccuracy: true
          });
        });
        
        location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };
      } catch (geoErr) {
        console.warn('위치 정보를 가져올 수 없습니다:', geoErr);
      }
      
      // 현재 강의 정보 가져오기 - classroomId를 얻기 위함
      const classInfo = classes.find(c => c.id === selectedClass || c._id === selectedClass);
      if (!classInfo) {
        toast.error('강의 정보를 찾을 수 없습니다.');
        return;
      }
      
      // 강의실 ID 설정
      const classroomId = classInfo.classroomId || 
                         classInfo.classroom?._id || 
                         classInfo.classroom?.id || 
                         selectedClass; // 최악의 경우 classId로 대체
      
      // 업데이트 데이터 준비
      const updateData = {
        method: convertMethodForBackend('manualEdit'),
        registeredBy: user?.id || user?._id,
        registeredByName: user?.name || user?.username || '관리자',
        registeredLocation: location,
        classroomId: classroomId // 반드시 classroomId 설정
      };
      
      // 입실/퇴실 시간 설정
      if (timeEditData.type === 'checkIn') {
        updateData.checkInTime = date.toISOString();
      } else {
        updateData.checkOutTime = date.toISOString();
      }
      
      // 출석 상태 자동 설정 (absent이거나 새 기록인 경우)
      if (attendance.status === 'absent' || attendance.isNew) {
        // 현재 입력중인 시간이 체크인인 경우
        if (timeEditData.type === 'checkIn') {
          // 퇴실 시간이 이미 있는지 확인
          if (attendance.checkOutTime && attendance.checkOutTime !== '-') {
            // 입실과 퇴실 시간이 모두 있으면 '출석'으로 설정
            updateData.status = convertStatusForBackend('present');
          } else {
            // 입실 시간만 있으면 '지각'으로 설정
            updateData.status = convertStatusForBackend('late');
          }
        } else {
          // 현재 입력중인 시간이 체크아웃인 경우
          
          // 입실 시간이 이미 있는지 확인
          if (attendance.checkInTime && attendance.checkInTime !== '-') {
            // 입실과 퇴실 시간이 모두 있으면 '출석'으로 설정
            updateData.status = convertStatusForBackend('present');
          } else {
            // 퇴실 시간만 있고 입실 시간이 없는 경우 처리
            // 이 경우 입실 시간도 자동으로 설정 (현재 시간 - 1시간)
            const checkInDate = new Date(date);
            checkInDate.setHours(checkInDate.getHours() - 1);
            updateData.checkInTime = checkInDate.toISOString();
            updateData.status = convertStatusForBackend('present');
            toast.info('입실 시간이 자동으로 설정되었습니다 (현재 시간 - 1시간)');
          }
        }
      }
      
      console.log('시간 수정 데이터:', updateData);
      
      // API 호출 및 응답 처리
      if (attendance.isNew) {
        // 새 출석 기록 생성
        updateData.studentId = attendance.studentId;
        updateData.classId = selectedClass;
        updateData.date = timeEditData.date;
        
        // 상태가 설정되지 않은 경우 기본값 설정
        if (!updateData.status) {
          updateData.status = convertStatusForBackend('late');
        }
        
        console.log('새 출석 기록 생성:', updateData);
        const response = await authAxios.post('/attendance', updateData);
        console.log('API 응답:', response.data);
        toast.success('새 출석 기록이 생성되었습니다.');
      } else {
        // 기존 출석 기록 업데이트
        console.log('기존 출석 기록 업데이트:', updateData);
        const response = await authAxios.put(`/attendance/${timeEditData.attendanceId}`, updateData);
        console.log('API 응답:', response.data);
        toast.success(`${timeEditData.type === 'checkIn' ? '입실' : '퇴실'} 시간이 업데이트되었습니다.`);
      }
      
      // 성공 시 모달 닫기 및 목록 새로고침
      setIsEditingTime(false);
      setTimeEditData({ 
        attendanceId: null, 
        studentId: null, 
        type: null, 
        time: '', 
        isNew: false,
        date: ''
      });
      
      await fetchAttendanceRecords();
    } catch (err) {
      console.error('시간 업데이트 오류:', err);
      if (err.response) {
        console.error('서버 응답:', err.response.data);
        console.error('상태 코드:', err.response.status);
        toast.error('서버 오류: ' + (err.response.data.message || '알 수 없는 오류'));
      } else {
        toast.error('시간 업데이트 중 오류가 발생했습니다.');
      }
    }
  };

  // 출석 생성 핸들러
  const onAddAttendance = async (data) => {
    try {
      // 상태 값을 백엔드 형식으로 변환
      const backendStatus = convertStatusForBackend(data.status);
      
      // 현재 강의 정보 가져오기 - classroomId를 얻기 위함
      const classInfo = classes.find(c => c.id === selectedClass || c._id === selectedClass);
      if (!classInfo) {
        toast.error('강의 정보를 찾을 수 없습니다.');
        return;
      }
      
      // 강의실 ID 설정
      const classroomId = classInfo.classroomId || 
                         classInfo.classroom?._id || 
                         classInfo.classroom?.id || 
                         selectedClass; // 최악의 경우 classId로 대체
      
      // 서버에 출석 상태 저장
      await authAxios.post('/attendance', {
        studentId: data.studentId,
        classId: selectedClass,
        classroomId: classroomId, // 필수 필드 추가
        status: backendStatus,
        method: convertMethodForBackend('manual'), // 올바른 enum 값 설정
        timestamp: new Date().toISOString(),
        date: new Date().toISOString().split('T')[0] // 날짜 필드 추가
      });
      
      // 성공 시 출석 목록 새로고침
      await fetchAttendanceRecords();
      
      toast.success('출석 상태가 저장되었습니다.');
      
      // 모달 닫기
      setIsAddingAttendance(false);
      reset();
    } catch (err) {
      console.error('Add attendance error:', err);
      
      // 오류 정보 상세 로깅
      if (err.response) {
        console.error('서버 응답:', err.response.data);
        console.error('상태 코드:', err.response.status);
        toast.error('서버 오류: ' + (err.response.data.message || '알 수 없는 오류'));
      } else {
      toast.error('출석 기록 추가 중 오류가 발생했습니다.');
      }
    }
  };

  // 출석 수정 핸들러
  const onEditAttendance = async (data) => {
    try {
      // 상태 값을 백엔드 형식으로 변환
      const backendStatus = convertStatusForBackend(data.status);
      
      // 현재 강의 정보 가져오기 - classroomId를 얻기 위함
      const classInfo = classes.find(c => c.id === selectedClass || c._id === selectedClass);
      if (!classInfo) {
        toast.error('강의 정보를 찾을 수 없습니다.');
        return;
      }
      
      // 강의실 ID 설정
      const classroomId = classInfo.classroomId || 
                         classInfo.classroom?._id || 
                         classInfo.classroom?.id || 
                         selectedClass; // 최악의 경우 classId로 대체
      
      // 서버에 출석 상태 저장
      await authAxios.put(`/attendance/${currentAttendance.id}`, {
        status: backendStatus,
        classroomId: classroomId, // 필수 필드 추가
        method: convertMethodForBackend('manualEdit'), // 올바른 enum 값 설정
        timestamp: new Date().toISOString()
      });
      
      // 성공 시 출석 목록 새로고침
      await fetchAttendanceRecords();
      
      // 성공 메시지 표시
      toast.success('출석 상태가 업데이트되었습니다.');
      
      // 모달 닫기
      setIsEditingAttendance(false);
      setCurrentAttendance(null);
      reset();
    } catch (err) {
      console.error('Edit attendance error:', err);
      
      // 오류 정보 상세 로깅
      if (err.response) {
        console.error('서버 응답:', err.response.data);
        console.error('상태 코드:', err.response.status);
        toast.error('서버 오류: ' + (err.response.data.message || '알 수 없는 오류'));
      } else {
      toast.error('출석 기록 수정 중 오류가 발생했습니다.');
      }
    }
  };

  // 출석 삭제
  const deleteAttendance = async (attendanceId) => {
    if (!window.confirm('이 출석 기록을 삭제하시겠습니까?')) {
      return;
    }
    
    try {
      // 서버에 삭제 요청
      await authAxios.delete(`/attendance/${attendanceId}`);
      
      // 성공 시 출석 목록 새로고침
      await fetchAttendanceRecords();
      
      toast.success('출석 기록이 삭제되었습니다.');
    } catch (err) {
      console.error('Error deleting attendance:', err);
      toast.error('출석 기록 삭제 중 오류가 발생했습니다.');
    }
  };

  // 학생을 강의에 등록
  const enrollStudent = async (studentId) => {
    if (!selectedClass) {
      toast.error('강의를 먼저 선택해주세요.');
      return;
    }
    
    if (!window.confirm('이 학생을 강의에 등록하시겠습니까?')) {
      return;
    }
    
    try {
      // 로딩 상태 설정
      setAttendanceLoading(true);
      
      // 서버에 학생 등록 요청
      await authAxios.post(`/classes/${selectedClass}/students`, {
        studentId: studentId
      });
      
      // 성공 시 학생 목록과 출석 기록 새로고침
      await fetchStudents();
      await fetchAttendanceRecords();
      
      toast.success('학생이 강의에 등록되었습니다.');
    } catch (err) {
      console.error('Error enrolling student:', err);
      
      // 오류 정보 상세 로깅
      if (err.response) {
        console.error('서버 응답:', err.response.data);
        console.error('상태 코드:', err.response.status);
        toast.error('서버 오류: ' + (err.response.data.message || '알 수 없는 오류'));
      } else {
        toast.error('학생 등록 중 오류가 발생했습니다.');
      }
    } finally {
      setAttendanceLoading(false);
    }
  };
  
  // 학생을 강의에서 등록 취소
  const unenrollStudent = async (studentId) => {
    if (!selectedClass) {
      toast.error('강의를 먼저 선택해주세요.');
      return;
    }
    
    if (!window.confirm('이 학생의 강의 등록을 취소하시겠습니까?')) {
      return;
    }
    
    try {
      // 로딩 상태 설정
      setAttendanceLoading(true);
      
      // 서버에 학생 등록 취소 요청
      await authAxios.delete(`/classes/${selectedClass}/students/${studentId}`);
      
      // 성공 시 학생 목록과 출석 기록 새로고침
      await fetchStudents();
      await fetchAttendanceRecords();
      
      toast.success('학생의 강의 등록이 취소되었습니다.');
    } catch (err) {
      console.error('Error unenrolling student:', err);
      
      // 오류 정보 상세 로깅
      if (err.response) {
        console.error('서버 응답:', err.response.data);
        console.error('상태 코드:', err.response.status);
        toast.error('서버 오류: ' + (err.response.data.message || '알 수 없는 오류'));
      } else {
        toast.error('학생 등록 취소 중 오류가 발생했습니다.');
      }
    } finally {
      setAttendanceLoading(false);
    }
  };
  
  // 수정 모드 시작
  const startEditAttendance = (attendance) => {
    setCurrentAttendance(attendance);
    setValue('status', attendance.status);
    setIsEditingAttendance(true);
  };

  // 일괄 상태 변경
  const handleBulkAction = async (action) => {
    if (!selectedClass) {
      toast.error('강의를 먼저 선택해주세요.');
      return;
    }
    
    if (!window.confirm('모든 학생의 출석 상태를 변경하시겠습니까?')) {
      return;
    }
    
    try {
      setAttendanceLoading(true);
      
      let status = '';
      switch (action) {
        case 'markPresent':
          status = 'present';
          break;
        case 'markLate':
          status = 'late';
          break;
        case 'markAbsent':
          status = 'absent';
          break;
        default:
          return;
      }
      
      // 상태 값을 백엔드 형식으로 변환
      const backendStatus = convertStatusForBackend(status);
      
      // 현재 강의 정보 가져오기 - classroomId를 얻기 위함
      const classInfo = classes.find(c => c.id === selectedClass || c._id === selectedClass);
      if (!classInfo) {
        toast.error('강의 정보를 찾을 수 없습니다.');
        return;
      }
      
      // 강의실 ID 설정
      const classroomId = classInfo.classroomId || 
                        classInfo.classroom?._id || 
                        classInfo.classroom?.id || 
                        selectedClass; // 최악의 경우 classId로 대체
      
      // 서버에 일괄 상태 변경 요청 - bulk 엔드포인트가 없으므로 개별 요청으로 처리
      const timestamp = new Date().toISOString();
      const today = new Date().toISOString().split('T')[0];
      
      // 모든 학생에 대해 개별적으로 상태 업데이트
      const updatePromises = attendanceList.map(async (attendance) => {
        try {
          if (attendance.isNew) {
            // 새 출석 기록 생성
            await authAxios.post('/attendance', {
              studentId: attendance.studentId,
              classId: selectedClass,
              classroomId: classroomId, // 필수 필드 추가
              status: backendStatus,
              method: convertMethodForBackend('admin'), // 올바른 enum 값 설정
              timestamp,
              date: today // 날짜 필드 추가
            });
          } else {
            // 기존 출석 기록 업데이트
            await authAxios.put(`/attendance/${attendance.id}`, {
              status: backendStatus,
              classroomId: classroomId, // 필수 필드 추가
              method: convertMethodForBackend('admin'), // 올바른 enum 값 설정
              timestamp
            });
          }
        } catch (error) {
          console.error(`Error updating attendance for student ${attendance.studentName}:`, error);
          // 개별 오류는 무시하고 계속 진행
        }
      });
      
      // 모든 업데이트 요청이 완료될 때까지 기다림
      await Promise.all(updatePromises);
      
      // 성공 시 출석 목록 새로고침
      await fetchAttendanceRecords();
      
      toast.success('모든 학생의 출석 상태가 업데이트되었습니다.');
    } catch (err) {
      console.error('Error in bulk update:', err);
      
      // 오류 정보 상세 로깅
      if (err.response) {
        console.error('서버 응답:', err.response.data);
        console.error('상태 코드:', err.response.status);
        toast.error('서버 오류: ' + (err.response.data.message || '알 수 없는 오류'));
      } else {
      toast.error('일괄 업데이트 중 오류가 발생했습니다.');
      }
    } finally {
      setAttendanceLoading(false);
    }
  };

  const resetAttendanceRecords = async () => {
    if (!selectedClass) {
      toast.error('강의를 먼저 선택해주세요.');
      return;
    }
    
    if (!window.confirm('현재 강의의 모든 출석 정보를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      return;
    }
    
    try {
      setAttendanceLoading(true);
      
      // 기존 출석 기록만 필터링 (isNew가 false인 것들)
      const existingRecords = attendanceList.filter(record => !record.isNew);
      
      // 모든 출석 기록 삭제
      const deletePromises = existingRecords.map(record => 
        authAxios.delete(`/attendance/${record.id}`)
      );
      
      await Promise.all(deletePromises);
      
      // 출석 기록 다시 불러오기
      await fetchAttendanceRecords();
      
      toast.success('모든 출석 기록이 삭제되었습니다.');
    } catch (err) {
      console.error('Error deleting attendance records:', err);
      toast.error('출석 기록 삭제 중 오류가 발생했습니다.');
    } finally {
      setAttendanceLoading(false);
    }
  };

  // 출석 상태 저장
  const saveAttendance = async (data) => {
    try {
      setIsSubmitting(true);

      // 현재 강의 정보 가져오기 - classroomId를 얻기 위함
      const classInfo = classes.find(c => c.id === selectedClass || c._id === selectedClass);
      if (!classInfo) {
        toast.error('강의 정보를 찾을 수 없습니다.');
        return;
      }
      
      // 강의실 ID 설정
      const classroomId = classInfo.classroomId || 
                         classInfo.classroom?._id || 
                         classInfo.classroom?.id || 
                         selectedClass; // 최악의 경우 classId로 대체

      // 서버에 출석 상태 저장
      await authAxios.post('/attendance', {
        studentId: data.studentId,
        classId: selectedClass,
        classroomId: classroomId, // 필수 필드 추가
        status: convertStatusForBackend(selectedStatus),
        method: convertMethodForBackend('manual'), // 올바른 enum 값 설정
        date: new Date().toISOString().split('T')[0], // 날짜 필드 추가
        time: new Date().toLocaleTimeString('ko-KR', { hour12: false })
      });

      // 성공 시 출석 목록 새로고침
      await fetchAttendanceRecords();
      
      // 성공 메시지 표시
      toast.success('출석 상태가 저장되었습니다.');
      
      // 모달 닫기
      setSelectedStudent(null);
      reset();
    } catch (err) {
      console.error('Error saving attendance:', err);
      
      // 오류 정보 상세 로깅
      if (err.response) {
        console.error('서버 응답:', err.response.data);
        console.error('상태 코드:', err.response.status);
        toast.error('서버 오류: ' + (err.response.data.message || '알 수 없는 오류'));
      } else {
      toast.error('출석 상태 저장 중 오류가 발생했습니다.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // QR 코드 생성
  const generateQRCode = async () => {
    try {
      setQrLoading(true);
      
      if (!selectedClass) {
        toast.error("QR 코드를 생성할 강의를 선택해주세요.");
        setQrLoading(false);
        return;
      }
      
      // 현재 강의 정보 가져오기
      const classInfo = classes.find(c => c.id === selectedClass || c._id === selectedClass);
      if (!classInfo) {
        toast.error("선택된 강의 정보를 찾을 수 없습니다.");
        setQrLoading(false);
        return;
      }
      
      console.log('선택된 강의 정보:', classInfo);
      
      // 강의실 정보 확인
      const classroomId = classInfo.classroomId || classInfo.classroom?._id || classInfo.classroom?.id;
      if (!classroomId) {
        toast.error("이 강의에 연결된 강의실 정보가 없습니다. 관리자에게 문의하세요.");
        setQrLoading(false);
        return;
      }
      
      // 현재 사용자 정보
      const instructorId = user?.userId || user?._id;
      
      // ClassroomManagement.js에서 사용하는 API 호출
      console.log(`강의실 ID(${classroomId})로 QR 코드 생성 요청`);
      const response = await authAxios.post(`/classrooms/${classroomId}/generate-qr`, {
        instructorId: instructorId,
        type: qrType // 'checkin' 또는 'checkout'
      });
      
      console.log('QR 코드 생성 응답:', response.data);
      
      // 반환된 QR 코드 정보 처리
      if (qrType === 'checkin') {
        setCurrentQRCode(response.data.checkinQR || response.data.checkinUrl);
      } else {
        setCurrentQRCode(response.data.checkoutQR || response.data.checkoutUrl);
      }
      
      // QR 코드 모달 표시
      setShowQRModal(true);
      setQrLoading(false);
    } catch (err) {
      console.error('QR 코드 생성 오류:', err);
      
      // 자세한 오류 정보 로깅
      if (err.response) {
        console.error('서버 응답:', err.response.data);
        console.error('상태 코드:', err.response.status);
        const errorMsg = err.response.data.message || '알 수 없는 오류';
        toast.error(`QR 코드 생성 실패: ${errorMsg}`);
      } else {
      toast.error('QR 코드 생성 중 오류가 발생했습니다.');
      }
      
      setQrLoading(false);
    }
  };
  
  // QR 코드 다운로드
  const downloadQRCode = () => {
    try {
      const svgElement = document.getElementById('qrcode-svg');
      if (!svgElement) {
        toast.error('QR 코드를 찾을 수 없습니다');
        return;
      }

      // SVG를 Canvas로 변환
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const img = new Image();
      
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        
        // Canvas를 이미지로 변환하여 다운로드
        const a = document.createElement('a');
        a.download = `출석QR코드_${classes.find(c => c.id === selectedClass || c._id === selectedClass)?.name || '강의'}_${new Date().toISOString().slice(0, 10)}.png`;
        a.href = canvas.toDataURL('image/png');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      };

      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    } catch (error) {
      console.error('QR 코드 다운로드 중 오류:', error);
      toast.error('QR 코드 다운로드 중 오류가 발생했습니다');
    }
  };
  
  // QR 코드 인쇄
  const printQRCode = () => {
    // 선택된 강의 정보 가져오기
    const classInfo = classes.find(c => c.id === selectedClass || c._id === selectedClass);
    // 강의실 이름 가져오기
    const classroomName = classInfo?.classroom?.name || classInfo?.classroomName || classInfo?.name || '강의실';
    // 강의실 주소 가져오기
    const classroomLocation = classInfo?.classroom?.location || classInfo?.location || {};
    const classroomAddress = classroomLocation?.address || '';
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>출석 QR 코드 인쇄</title>
          <style>
            body { 
              font-family: Arial, sans-serif;
              text-align: center;
              padding: 20px;
            }
            .qr-container {
              margin: 0 auto;
              max-width: 400px;
            }
            .qr-url {
              word-break: break-all;
              margin: 15px 0;
            }
            svg {
              display: block;
              margin: 0 auto;
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <h2>출석 QR 코드</h2>
            <p>강의실: ${classroomName}</p>
            ${classroomAddress ? `<p>주소: ${classroomAddress}</p>` : ''}
            <p>유효시간: 30분</p>
            <div>${document.getElementById('qrcode-svg')?.outerHTML || ''}</div>
            <p class="qr-url">URL: ${currentQRCode}</p>
          </div>
          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };
  
  // QR 코드 모달 렌더링
  const renderQRCodeModal = () => {
    // 선택된 강의 정보 가져오기
    const classInfo = classes.find(c => c.id === selectedClass || c._id === selectedClass);
    // 강의실 이름 가져오기
    const classroomName = classInfo?.classroom?.name || classInfo?.classroomName || classInfo?.name || '강의실';
    // 강의실 주소 가져오기
    const classroomLocation = classInfo?.classroom?.location || classInfo?.location || {};
    const classroomAddress = classroomLocation?.address || '';
    
    return (
      <Modal
        title={`${classroomName} ${qrType === 'checkin' ? '입실' : '퇴실'} QR 코드`}
        open={showQRModal}
        onCancel={() => setShowQRModal(false)}
        footer={null}
        width={400}
        centered
      >
        <div className="flex flex-col items-center">
          {currentQRCode ? (
            <>
              <div className="text-center mb-4">
                <p className="text-lg font-bold">
                  {classroomName}
                </p>
                <p className="text-sm text-gray-500">
                  {qrType === 'checkin' ? '입실' : '퇴실'} QR 코드
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  생성 시간: {new Date().toLocaleString()}
                </p>
                {classroomAddress && (
                  <p className="text-xs text-gray-400 mt-1">
                    주소: {classroomAddress}
                  </p>
                )}
              </div>
              
              <div className="mb-4" id="qrcode-svg">
                <QRCodeSVG 
                  value={currentQRCode}
                  size={250}
                  level="H"
                />
              </div>
              
              <div className="flex space-x-2 mb-4">
                <button
                  className="flex items-center px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  onClick={downloadQRCode}
                >
                  <DownloadOutlined style={{ marginRight: 5 }} />
                  다운로드
                </button>
                
                <button
                  className="flex items-center px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  onClick={printQRCode}
                >
                  <PrinterOutlined style={{ marginRight: 5 }} />
                  인쇄
                </button>
              </div>
            </>
          ) : (
            <div className="w-[250px] h-[250px] flex items-center justify-center bg-gray-100">
              <p className="text-gray-500">QR 코드 데이터가 없습니다.</p>
            </div>
          )}
        </div>
      </Modal>
    );
  };

  // 새로고침 함수
  const refreshAttendance = () => {
    if (selectedClass) {
      fetchAttendanceRecords();
      toast.success('출석 목록이 새로고침되었습니다.');
    } else {
      toast.error('먼저 강의를 선택해주세요.');
    }
  };

  // 출석 상태 표시 이름
  const getStatusDisplayName = (status) => {
    const statusNames = {
      'present': '출석',
      'absent': '결석',
      'late': '지각',
      'earlyLeave': '조퇴'
    };
    
    return statusNames[status] || '미정';
  };

  // 출석 방법 표시 이름
  const getMethodDisplayName = (method) => {
    const methodNames = {
      'qr': 'QR인증',
      'admin': '관리자등록',
      'manual': '수기등록',
      'manualEdit': '수기변경'
    };
    
    return methodNames[method] || '미정';
  };

  // 출석 상태별 색상
  const getStatusColor = (status) => {
    const statusColors = {
      'present': 'bg-green-100 text-green-800',
      'absent': 'bg-red-100 text-red-800',
      'late': 'bg-yellow-100 text-yellow-800',
      'earlyLeave': 'bg-blue-100 text-blue-800'
    };
    
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  // 강의 목록 정렬 함수
  const requestClassSort = (key) => {
    let direction = 'asc';
    if (classSortConfig.key === key && classSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setClassSortConfig({ key, direction });
  };

  // 필터링된 강의 목록
  const filteredClasses = useMemo(() => {
    let filtered = [...classes];
    
    // 검색어 필터링
    if (classSearchQuery) {
      const query = classSearchQuery.toLowerCase();
      filtered = filtered.filter(classItem => 
        classItem.name.toLowerCase().includes(query) ||
        (classItem.instructorName && classItem.instructorName.toLowerCase().includes(query))
      );
    }
    
    // 분류 필터링
    if (classFilter.category !== 'all') {
      filtered = filtered.filter(classItem => classItem.category === classFilter.category);
    }
    
    // 강사 필터링
    if (classFilter.instructor !== 'all') {
      filtered = filtered.filter(classItem => 
        classItem.instructorId === classFilter.instructor || 
        (typeof classItem.instructorId === 'object' && 
         (classItem.instructorId.id === classFilter.instructor || 
          classItem.instructorId._id === classFilter.instructor)) ||
        classItem.instructorName === classFilter.instructor
      );
    }
    
    // 강의실 필터링
    if (classFilter.classroom !== 'all') {
      filtered = filtered.filter(classItem => 
        classItem.classroom?.name === classFilter.classroom || 
        classItem.classroomName === classFilter.classroom
      );
    }
    
    // 정렬 적용
    if (classSortConfig.key) {
      filtered.sort((a, b) => {
        // null 값 처리
        if (!a[classSortConfig.key]) return classSortConfig.direction === 'asc' ? -1 : 1;
        if (!b[classSortConfig.key]) return classSortConfig.direction === 'asc' ? 1 : -1;
        
        // 타입에 따른 정렬
        if (typeof a[classSortConfig.key] === 'string') {
          if (classSortConfig.direction === 'asc') {
            return a[classSortConfig.key].localeCompare(b[classSortConfig.key]);
          } else {
            return b[classSortConfig.key].localeCompare(a[classSortConfig.key]);
          }
        } else {
          if (classSortConfig.direction === 'asc') {
            return a[classSortConfig.key] - b[classSortConfig.key];
          } else {
            return b[classSortConfig.key] - a[classSortConfig.key];
          }
        }
      });
    }
    
    return filtered;
  }, [classes, classSearchQuery, classFilter, classSortConfig]);

  // 출석 목록 UI 표시 부분
  return (
    <div className="flex h-full">
      {/* 좌측 강의 목록 패널 */}
      <div className="hidden md:block w-[40rem] bg-gray-100 border-r overflow-y-auto">
        <div className="p-4">
          <div className="mb-4">
            <h2 className="text-lg font-semibold">
              강의 목록
              {isInstructor && <span className="text-sm font-normal text-gray-500 ml-2">(강사 전용)</span>}
            </h2>
          </div>
          
          {/* 강의 검색 및 필터 */}
          <div className="mb-4 space-y-2">
            <input
              type="text"
              placeholder="강의명 또는 강사 검색..."
              className="w-full p-2 border rounded text-sm"
              value={classSearchQuery}
              onChange={(e) => setClassSearchQuery(e.target.value)}
            />
            
            <div className="flex space-x-2">
              <select
                className="flex-1 p-2 border rounded text-sm"
                value={classFilter.category}
                onChange={(e) => setClassFilter({...classFilter, category: e.target.value})}
              >
                <option value="all">모든 분류</option>
                {[...new Set(classes.map(c => c.category))].filter(Boolean).map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              
              <select
                className="flex-1 p-2 border rounded text-sm"
                value={classFilter.instructor}
                onChange={(e) => setClassFilter({...classFilter, instructor: e.target.value})}
              >
                <option value="all">모든 강사</option>
                {[...new Set(classes.map(c => c.instructorName))].filter(Boolean).map(instructor => (
                  <option key={instructor} value={instructor}>{instructor}</option>
                ))}
              </select>
              
              <select
                className="flex-1 p-2 border rounded text-sm"
                value={classFilter.classroom}
                onChange={(e) => setClassFilter({...classFilter, classroom: e.target.value})}
              >
                <option value="all">모든 강의실</option>
                {[...new Set(classes.map(c => c.classroom?.name || c.classroomName).filter(Boolean))].map(classroom => (
                  <option key={classroom} value={classroom}>{classroom}</option>
                ))}
              </select>
            </div>
          </div>
          
          {classesLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-gray-100">
                <thead>
                  <tr>
                    <th 
                      className="py-2 px-2 text-left text-xs font-medium text-gray-600 cursor-pointer"
                      onClick={() => requestClassSort('name')}
                    >
                      강의명
                      {classSortConfig.key === 'name' && (
                        classSortConfig.direction === 'asc' 
                          ? <SortAscendingOutlined className="ml-1" /> 
                          : <SortDescendingOutlined className="ml-1" />
                      )}
                    </th>
                    <th 
                      className="py-2 px-2 text-left text-xs font-medium text-gray-600 cursor-pointer"
                      onClick={() => requestClassSort('category')}
                    >
                      분류
                      {classSortConfig.key === 'category' && (
                        classSortConfig.direction === 'asc' 
                          ? <SortAscendingOutlined className="ml-1" /> 
                          : <SortDescendingOutlined className="ml-1" />
                      )}
                    </th>
                    <th 
                      className="py-2 px-2 text-left text-xs font-medium text-gray-600 cursor-pointer"
                      onClick={() => requestClassSort('date')}
                    >
                      날짜
                      {classSortConfig.key === 'date' && (
                        classSortConfig.direction === 'asc' 
                          ? <SortAscendingOutlined className="ml-1" /> 
                          : <SortDescendingOutlined className="ml-1" />
                      )}
                    </th>
                    <th 
                      className="py-2 px-2 text-left text-xs font-medium text-gray-600 cursor-pointer"
                      onClick={() => requestClassSort('classroomName')}
                    >
                      강의실
                      {classSortConfig.key === 'classroomName' && (
                        classSortConfig.direction === 'asc' 
                          ? <SortAscendingOutlined className="ml-1" /> 
                          : <SortDescendingOutlined className="ml-1" />
                      )}
                    </th>
                    <th 
                      className="py-2 px-2 text-left text-xs font-medium text-gray-600 cursor-pointer"
                      onClick={() => requestClassSort('instructorName')}
                    >
                      강사
                      {classSortConfig.key === 'instructorName' && (
                        classSortConfig.direction === 'asc' 
                          ? <SortAscendingOutlined className="ml-1" /> 
                          : <SortDescendingOutlined className="ml-1" />
                      )}
                    </th>
                    <th 
                      className="py-2 px-2 text-center text-xs font-medium text-gray-600 cursor-pointer"
                      onClick={() => requestClassSort('attendanceStats.total')}
                    >
                      전체
                      {classSortConfig.key === 'attendanceStats.total' && (
                        classSortConfig.direction === 'asc' 
                          ? <SortAscendingOutlined className="ml-1" /> 
                          : <SortDescendingOutlined className="ml-1" />
                      )}
                    </th>
                    <th 
                      className="py-2 px-2 text-center text-xs font-medium text-gray-600 cursor-pointer"
                      onClick={() => requestClassSort('attendanceStats.present')}
                    >
                      출석
                      {classSortConfig.key === 'attendanceStats.present' && (
                        classSortConfig.direction === 'asc' 
                          ? <SortAscendingOutlined className="ml-1" /> 
                          : <SortDescendingOutlined className="ml-1" />
                      )}
                    </th>
                    <th 
                      className="py-2 px-2 text-center text-xs font-medium text-gray-600 cursor-pointer"
                      onClick={() => requestClassSort('attendanceStats.absent')}
                    >
                      결석
                      {classSortConfig.key === 'attendanceStats.absent' && (
                        classSortConfig.direction === 'asc' 
                          ? <SortAscendingOutlined className="ml-1" /> 
                          : <SortDescendingOutlined className="ml-1" />
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClasses.map(classItem => (
                    <tr 
                      key={classItem.id || classItem._id} 
                      className={`cursor-pointer hover:bg-gray-200 ${selectedClass === (classItem.id || classItem._id) ? 'bg-blue-100' : ''}`}
                      onClick={() => {
                        setSelectedClass(classItem.id || classItem._id);
                        // 선택된 강의 ID를 로컬 스토리지에 저장
                        localStorage.setItem('selectedClassId', classItem.id || classItem._id);
                      }}
                    >
                      <td className="py-2 px-2 border-t">
                        <div className="font-medium text-sm">{classItem.name}</div>
                      </td>
                      <td className="py-2 px-2 border-t">
                        <div className="text-xs text-gray-500">
                          {classItem.category || '미지정'}
                        </div>
                      </td>
                      <td className="py-2 px-2 border-t">
                        <div className="text-xs text-gray-500">
                          {classItem.date ? new Date(classItem.date).toLocaleDateString('ko-KR') : '-'}
                        </div>
                      </td>
                      <td className="py-2 px-2 border-t">
                        <div className="text-xs text-gray-500">
                          {classItem.classroom?.name || 
                           classItem.classroomName || 
                           (typeof classItem.classroom === 'string' ? classItem.classroom : 
                           (classItem.classroom?.location?.address ? `${classItem.classroom.name} (${classItem.classroom.location.address})` : '-'))}
                        </div>
                      </td>
                      <td className="py-2 px-2 border-t">
                        <div className="text-xs text-gray-500">
                          {classItem.instructorName || '-'}
                        </div>
                      </td>
                      <td className="py-2 px-2 border-t text-center">
                        <div className="text-xs font-medium">
                          {classItem.attendanceStats?.total || 0}
                        </div>
                      </td>
                      <td className="py-2 px-2 border-t text-center">
                        <div className="text-xs font-medium text-green-600">
                          {classItem.attendanceStats?.present || 0}
                        </div>
                      </td>
                      <td className="py-2 px-2 border-t text-center">
                        <div className="text-xs font-medium text-red-600">
                          {classItem.attendanceStats?.absent || 0}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      {/* 메인 컨텐츠 영역 - 모바일에서는 전체 화면 사용 */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="p-4 bg-white border-b">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
            <div>
          <h1 className="text-2xl font-bold">출석 관리</h1>
          {selectedClass && (
            <div className="mt-1 text-gray-600 text-sm">
              <span className="font-semibold">{classes.find(c => c.id === selectedClass || c._id === selectedClass)?.name || '선택된 강의'}</span>
              <span className="mx-2">|</span>
              <span>분류: {classes.find(c => c.id === selectedClass || c._id === selectedClass)?.category || '-'}</span>
              <span className="mx-2">|</span>
              <span>날짜: {classes.find(c => c.id === selectedClass || c._id === selectedClass)?.date ? new Date(classes.find(c => c.id === selectedClass || c._id === selectedClass)?.date).toLocaleDateString('ko-KR') : '-'}</span>
              <span className="mx-2">|</span>
              <span>강의실: {classes.find(c => c.id === selectedClass || c._id === selectedClass)?.classroom?.name || 
                          classes.find(c => c.id === selectedClass || c._id === selectedClass)?.classroomName || '-'}</span>
              <span className="mx-2">|</span>
              <span>강사: {classes.find(c => c.id === selectedClass || c._id === selectedClass)?.instructorName || '-'}</span>
            </div>
          )}
            </div>
            
            <div className="mt-2 sm:mt-0 text-right">
              <p className="text-sm text-gray-600">
                <span className="font-semibold">{user?.name || user?.username}</span> 님 안녕하세요.
              </p>
              <p className="text-xs text-gray-500">
                {user?.roles?.includes('admin') ? '관리자' : 
                 user?.roles?.includes('instructor') ? '강사' : '사용자'}
              </p>
            </div>
          </div>
          
          {/* 모바일에서만 보이는 강의 선택 드롭다운 */}
          <div className="block md:hidden mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              강의 선택
              {isInstructor && <span className="text-xs text-blue-600 ml-2">[강사 전용 강의]</span>}
            </label>
            {classesLoading ? (
              <div className="py-2 px-4 border rounded bg-gray-50">
                <span className="text-gray-400">로딩 중...</span>
              </div>
            ) : (
              <select 
                className="w-full p-2 border rounded"
                value={selectedClass || ''}
                onChange={(e) => setSelectedClass(e.target.value)}
              >
                <option value="">강의를 선택하세요</option>
                {classes.length === 0 ? (
                  <option value="" disabled>표시할 강의가 없습니다</option>
                ) : (
                  classes.map(classItem => (
                    <option key={classItem.id || classItem._id} value={classItem.id || classItem._id}>
                      {classItem.name} ({classItem.category || '미지정'}) - {classItem.classroom?.name || 
                      classItem.classroomName || '강의실 미지정'}
                    </option>
                  ))
                )}
              </select>
            )}
            {user?.roles?.includes('instructor') && classes.length === 0 && (
              <p className="text-xs text-red-500 mt-1">
                강사로 등록된 강의가 없습니다. 관리자에게 문의하세요.
              </p>
            )}
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {!selectedClass ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <p className="text-gray-500">좌측에서 강의를 선택해주세요.</p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md p-6">
              {/* QR 코드 생성 영역 (강사인 경우만 표시) */}
              {isInstructor && (
                <div className="mb-6">
                  <div className="flex flex-wrap items-center gap-4">
                    <div>
                      <label className="block mb-1 text-sm font-medium">QR 코드 유형</label>
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          className={`px-4 py-2 rounded ${qrType === 'checkin' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                          onClick={() => setQrType('checkin')}
                        >
                          입실
                        </button>
                        <button
                          type="button"
                          className={`px-4 py-2 rounded ${qrType === 'checkout' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                          onClick={() => setQrType('checkout')}
                        >
                          퇴실
                        </button>
                      </div>
                    </div>
                    
                    <button
                      type="button"
                      onClick={generateQRCode}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                      disabled={qrLoading}
                    >
                      {qrLoading ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          생성 중...
                        </span>
                      ) : `${qrType === 'checkin' ? '입실' : '퇴실'} QR 코드 생성`}
                    </button>
                  </div>
                </div>
              )}
              
              {/* 출석 요약 정보 */}
              {attendanceList.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <p className="text-lg font-bold text-blue-700">{attendanceSummary.total}</p>
                    <p className="text-sm text-blue-600">전체 인원</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg text-center">
                    <p className="text-lg font-bold text-green-700">{attendanceSummary.present}</p>
                    <p className="text-sm text-green-600">출석</p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg text-center">
                    <p className="text-lg font-bold text-yellow-700">{attendanceSummary.late}</p>
                    <p className="text-sm text-yellow-600">지각</p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg text-center">
                    <p className="text-lg font-bold text-red-700">{attendanceSummary.absent}</p>
                    <p className="text-sm text-red-600">결석</p>
                  </div>
                </div>
              )}
              
              {/* 기수별 탭 */}
              <div className="flex border-b mb-4 overflow-x-auto">
                {cohortTabs.map(tab => (
                  <button
                    key={tab}
                    className={`px-4 py-2 font-medium ${activeTab === tab ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab === 'all' ? '전체' : `${tab}기`}
                  </button>
                ))}
              </div>
              
              {/* 검색 및 필터 */}
              <div className="flex flex-wrap gap-4 mb-6">
                {/* <div className="flex-1 min-w-[200px]">
                  <input
                    type="text"
                    placeholder="학생 이름 검색..."
                    className="w-full p-2 border rounded"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                
                <div>
                  <select
                    className="w-full p-2 border rounded"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">모든 상태</option>
                    <option value="present">출석</option>
                    <option value="late">지각</option>
                    <option value="earlyLeave">조퇴</option>
                    <option value="absent">결석</option>
                  </select>
                </div>
                
                <div>
                  <select
                    className="w-full p-2 border rounded"
                    value={methodFilter}
                    onChange={(e) => setMethodFilter(e.target.value)}
                  >
                    <option value="all">모든 등록방법</option>
                    <option value="qr">QR인증</option>
                    <option value="admin">관리자등록</option>
                    <option value="manual">수기등록</option>
                    <option value="manualEdit">수기변경</option>
                  </select>
                </div> */}
                
                <button
                  type="button"
                  onClick={refreshAttendance}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  새로고침
                </button>
              </div>
              
              {/* 출석 목록 테이블 */}
              {attendanceLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="py-2 px-4 text-left">
                          <div className="flex items-center cursor-pointer" onClick={() => requestAttendanceSort('studentName')}>
                            학생 이름
                            {attendanceSortConfig.key === 'studentName' && (
                              attendanceSortConfig.direction === 'asc' 
                                ? <SortAscendingOutlined className="ml-1" /> 
                                : <SortDescendingOutlined className="ml-1" />
                            )}
                          </div>
                        </th>
                        <th className="py-2 px-4 text-left">
                          <div className="flex items-center cursor-pointer" onClick={() => requestAttendanceSort('status')}>
                            출석 상태
                            {attendanceSortConfig.key === 'status' && (
                              attendanceSortConfig.direction === 'asc' 
                                ? <SortAscendingOutlined className="ml-1" /> 
                                : <SortDescendingOutlined className="ml-1" />
                            )}
                          </div>
                        </th>
                        <th className="py-2 px-4 text-left cursor-pointer" onClick={() => requestAttendanceSort('checkInTime')}>
                          <div className="flex items-center">
                            입실 시간
                            {attendanceSortConfig.key === 'checkInTime' && (
                              attendanceSortConfig.direction === 'asc' 
                                ? <SortAscendingOutlined className="ml-1" /> 
                                : <SortDescendingOutlined className="ml-1" />
                            )}
                          </div>
                        </th>
                        <th className="py-2 px-4 text-left cursor-pointer" onClick={() => requestAttendanceSort('checkOutTime')}>
                          <div className="flex items-center">
                            퇴실 시간
                            {attendanceSortConfig.key === 'checkOutTime' && (
                              attendanceSortConfig.direction === 'asc' 
                                ? <SortAscendingOutlined className="ml-1" /> 
                                : <SortDescendingOutlined className="ml-1" />
                            )}
                          </div>
                        </th>
                        <th className="py-2 px-4 text-left cursor-pointer" onClick={() => requestAttendanceSort('method')}>
                          <div className="flex items-center">
                            등록 방법
                            {attendanceSortConfig.key === 'method' && (
                              attendanceSortConfig.direction === 'asc' 
                                ? <SortAscendingOutlined className="ml-1" /> 
                                : <SortDescendingOutlined className="ml-1" />
                            )}
                          </div>
                        </th>
                        <th className="py-2 px-4 text-left cursor-pointer" onClick={() => requestAttendanceSort('registeredByName')}>
                          <div className="flex items-center">
                            등록자
                            {attendanceSortConfig.key === 'registeredByName' && (
                              attendanceSortConfig.direction === 'asc' 
                                ? <SortAscendingOutlined className="ml-1" /> 
                                : <SortDescendingOutlined className="ml-1" />
                            )}
                          </div>
                        </th>
                        <th className="py-2 px-4 text-center">관리</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedClassroomStudents.length > 0 ? (
                        // 강의실에 속한 모든 학생 목록 표시 (등록된 학생 먼저)
                        sortedClassroomStudents.map(student => {
                          // 학생 객체에서 정보 추출
                          const isEnrolled = student.isEnrolled;
                          const attendanceRecord = student.attendanceRecord;
                          
                          // 비활성화된 학생(미등록)의 스타일 
                          const disabledStyle = !isEnrolled ? 'opacity-50 bg-gray-50' : '';
                          
                          return (
                            <tr 
                              key={student.id || student._id} 
                              className={`border-b hover:bg-gray-50 ${disabledStyle}`}
                            >
                              <td className="py-2 px-4">
                                <div className="flex items-center">
                                  <span>{student.name}</span>
                                  {!isEnrolled && (
                                    <span className="ml-2 text-xs text-gray-500 bg-gray-200 px-1 rounded">미등록</span>
                                  )}
                                </div>
                              </td>
                              <td className="py-2 px-4">
                                <span className={`inline-block px-2 py-1 rounded text-xs ${attendanceRecord ? getStatusColor(attendanceRecord.status) : 'bg-gray-100 text-gray-800'}`}>
                                  {attendanceRecord ? getStatusDisplayName(attendanceRecord.status) : '미등록'}
                                </span>
                              </td>
                              <td 
                                className={`py-2 px-4 ${isEnrolled ? 'cursor-pointer hover:bg-gray-100' : ''}`} 
                                onClick={() => isEnrolled && attendanceRecord && handleTimeClick(attendanceRecord, 'checkIn')}
                              >
                                {attendanceRecord?.checkInTime || '-'}
                              </td>
                              <td 
                                className={`py-2 px-4 ${isEnrolled ? 'cursor-pointer hover:bg-gray-100' : ''}`}
                                onClick={() => isEnrolled && attendanceRecord && handleTimeClick(attendanceRecord, 'checkOut')}
                              >
                                {attendanceRecord?.checkOutTime || '-'}
                              </td>
                              <td className="py-2 px-4">
                                {attendanceRecord && (attendanceRecord.checkInTime !== '-' || attendanceRecord.checkOutTime !== '-') 
                                  ? getMethodDisplayName(attendanceRecord.method) 
                                  : ''}
                              </td>
                              <td className="py-2 px-4">
                                {attendanceRecord && (attendanceRecord.checkInTime !== '-' || attendanceRecord.checkOutTime !== '-') 
                                  ? attendanceRecord.registeredByName || ''
                                  : ''}
                                {attendanceRecord?.locationInfo && (attendanceRecord.checkInTime !== '-' || attendanceRecord.checkOutTime !== '-') && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    GPS: {attendanceRecord.locationInfo}
                                  </div>
                                )}
                              </td>
                              <td className="py-2 px-4 text-center">
                                <div className="flex justify-center space-x-1">
                                  {isEnrolled ? (
                                    // 등록된 학생에게는 출석/결석/등록취소 버튼 표시
                                    <>
                                      <button
                                        title="출석"
                                        className={`p-1 rounded ${attendanceRecord?.status === 'present' ? 'bg-green-100 text-green-700' : 'bg-gray-100 hover:bg-green-100'}`}
                                        onClick={() => attendanceRecord && handleStatusChange(attendanceRecord, 'present')}
                                      >
                                        <CheckIcon />
                                      </button>
                                      <button
                                        title="결석"
                                        className={`p-1 rounded ${attendanceRecord?.status === 'absent' ? 'bg-red-100 text-red-700' : 'bg-gray-100 hover:bg-red-100'}`}
                                        onClick={() => attendanceRecord && handleStatusChange(attendanceRecord, 'absent')}
                                      >
                                        <AbsentIcon />
                                      </button>
                                      <button
                                        title="등록 취소"
                                        className="p-1 px-2 bg-gray-100 text-gray-700 rounded hover:bg-red-100"
                                        onClick={() => unenrollStudent(student.id || student._id)}
                                      >
                                        등록취소
                                      </button>
                                    </>
                                  ) : (
                                    // 미등록 학생에게는 등록 버튼 표시
                                    <button
                                      title="수강생 등록"
                                      className="p-1 px-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                      onClick={() => enrollStudent(student.id || student._id)}
                                    >
                                      추가
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center py-8">
                            <p className="text-gray-500">교육생 정보가 없습니다.</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* 시간 수정 모달 */}
      <Modal
        title={`${timeEditData.type === 'checkIn' ? '입실' : '퇴실'} 시간 수정`}
        open={isEditingTime}
        onCancel={() => setIsEditingTime(false)}
        footer={null}
      >
        <form onSubmit={handleTimeSubmit} className="mt-4">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">시간</label>
            <input
              type="time"
              className="w-full p-2 border rounded"
              value={timeEditData.time}
              onChange={(e) => setTimeEditData({...timeEditData, time: e.target.value})}
              required
            />
          </div>
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setIsEditingTime(false)}
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              취소
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              저장
            </button>
          </div>
        </form>
      </Modal>
      
      {/* QR 코드 모달 */}
      {renderQRCodeModal()}
    </div>
  );
};

export default AttendanceManagement; 